<script lang="ts">
	import CheckIcon from '@lucide/svelte/icons/check';
	import KeyRoundIcon from '@lucide/svelte/icons/key-round';
	import LoaderCircleIcon from '@lucide/svelte/icons/loader-circle';
	import CapabilityLogo from '$lib/components/CapabilityLogo.svelte';
	import { presentation, CATEGORY_ORDER } from '$lib/capability-presentation';
	import { buildCards, type Card } from '$lib/marketplace-helpers';
	import type { CatalogEntry, Me } from '$lib/marketplace-api';

	type Props = {
		catalog: CatalogEntry[];
		catalogLoading: boolean;
		picked: Set<string>;
		isAdmin: boolean;
		onToggle: (id: string) => void;
		onNext: () => void;
		onBack: () => void;
	};
	let { catalog, catalogLoading, picked, isAdmin, onToggle, onNext, onBack }: Props = $props();

	// Same model as /marketplace (B): read/write twins MERGED into one card per
	// brand, official logos, NO "read-only / can make changes" jargon pills, and
	// admin-only tools hidden for members (buildCards drops them). Onboarding stays
	// "pick" mode: tapping selects the brand's safe read-only id for install at
	// Finish; write + the key are configured later on /marketplace.
	const me = $derived<Me>({ user: '', isAdmin, onboarded: false });
	// buildCards already drops admin-locked (non-installed) caps. Also drop an
	// installed-but-now-unavailable one (unavailableReason='admin_off' — admin turned
	// it off / revoked access): onboarding is a pick surface, so it must not offer a
	// tool the viewer can't actually use, even one a force-install left behind.
	const cards = $derived(
		buildCards(catalog, me).filter((c) => c.kind === 'mcp' && !c.unavailableReason),
	);
	const groups = $derived.by(() => {
		const buckets = new Map<string, Card[]>();
		for (const c of cards) {
			const cat = presentation(c.key)?.category ?? 'Other';
			const arr = buckets.get(cat);
			if (arr) arr.push(c);
			else buckets.set(cat, [c]);
		}
		const out: { category: string; items: Card[] }[] = [];
		for (const cat of [...CATEGORY_ORDER, 'Other']) {
			const items = buckets.get(cat);
			if (items?.length) out.push({ category: cat === 'Other' ? 'More' : cat, items });
		}
		return out;
	});

	function handleToggle(c: Card): void {
		onToggle(c.primaryId);
	}
	function handleKeydown(e: KeyboardEvent, c: Card): void {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			handleToggle(c);
		}
	}
</script>

<div class="flex flex-col gap-9">
	<div class="flex items-baseline justify-between gap-3">
		<h2 class="text-[26px] font-medium tracking-[-0.02em] text-[color:var(--fg-strong)]">Tools</h2>
		<span class="text-[12px] tabular-nums text-[color:var(--muted-foreground)]">
			<strong class="font-medium text-[color:var(--fg-strong)]">{picked.size}</strong> picked
		</span>
	</div>

	{#if catalogLoading}
		<div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
			{#each Array(6) as _, i (i)}
				<div class="h-[92px] animate-pulse rounded-[14px] border" style="background: var(--glass); border-color: var(--glass-border);"></div>
			{/each}
		</div>
	{:else}
		<div class="flex flex-col gap-8">
			{#each groups as group (group.category)}
				<div class="flex flex-col gap-3.5">
					<h3 class="px-0.5 text-[11px] font-medium uppercase tracking-[0.2em] text-[color:var(--fg-faint)]">
						{group.category}
					</h3>
					<div class="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
						{#each group.items as card (card.key)}
							{@const isPicked = picked.has(card.primaryId)}
							<div
								role="button"
								tabindex={0}
								aria-pressed={isPicked}
								onclick={() => handleToggle(card)}
								onkeydown={(e) => handleKeydown(e, card)}
								class="group relative cursor-pointer rounded-[14px] border p-[18px] transition-all focus:outline-none"
								style="
									background: {isPicked
									? 'linear-gradient(180deg, color-mix(in srgb, var(--primary) 6%, transparent), color-mix(in srgb, var(--primary) 2%, transparent)), var(--glass)'
									: 'var(--glass)'};
									border-color: {isPicked ? 'color-mix(in srgb, var(--primary) 45%, transparent)' : 'var(--glass-border)'};
									backdrop-filter: blur(18px) saturate(130%);
									-webkit-backdrop-filter: blur(18px) saturate(130%);
									box-shadow: {isPicked
									? '0 0 0 1px color-mix(in srgb, var(--primary) 18%, transparent) inset, 0 18px 50px -22px var(--glow), 0 0 60px -20px var(--glow)'
									: 'none'};
								"
							>
								<div class="flex items-center gap-3 pr-7">
									<CapabilityLogo brand={card.brand ?? null} />
									<div class="min-w-0 flex-1">
										<p class="m-0 truncate text-[14px] font-medium tracking-[-0.01em] text-[color:var(--fg-strong)]">
											{card.name}
										</p>
										<p class="mt-0.5 truncate text-[12px] text-[color:var(--muted-foreground)]">
											{card.essence}
										</p>
									</div>
								</div>

								{#if card.requiresConnection}
									<div class="mt-3 inline-flex items-center gap-1 text-[10.5px] text-[color:var(--fg-faint)]">
										<KeyRoundIcon class="size-2.5" />
										Needs your key — connect later
									</div>
								{/if}

								<span
									class="pointer-events-none absolute right-[14px] top-[14px] grid size-[18px] place-items-center rounded-full bg-primary text-primary-foreground"
									style="transform: scale({isPicked ? 1 : 0}); transition: transform .25s cubic-bezier(.4,1.6,.4,1); box-shadow: 0 0 18px var(--glow);"
									aria-hidden="true"
								>
									<CheckIcon class="size-[10px]" strokeWidth={3} />
								</span>
							</div>
						{/each}
					</div>
				</div>
			{/each}
		</div>
	{/if}

	<div class="flex items-center justify-between gap-3 pt-1">
		<span class="text-[12px] text-[color:var(--fg-faint)]">Zero is fine.</span>
		<div class="flex items-center gap-2">
			<button
				type="button"
				onclick={onBack}
				class="inline-flex items-center justify-center rounded-full border px-5 py-2 text-[13px] font-medium text-[color:var(--muted-foreground)] backdrop-blur-md transition-colors hover:text-[color:var(--fg-strong)]"
				style="background: var(--glass); border-color: var(--glass-border);"
			>
				Back
			</button>
			<button
				type="button"
				onclick={onNext}
				disabled={catalogLoading}
				class="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-[color:var(--primary)]/90 disabled:opacity-60"
				style="box-shadow: 0 8px 28px -10px var(--glow), 0 0 0 1px rgba(255,255,255,0.06) inset;"
			>
				{#if catalogLoading}
					<LoaderCircleIcon class="size-3.5 animate-spin" />
				{/if}
				Continue
			</button>
		</div>
	</div>
</div>
